import React from 'react';
import { Banknote, CreditCard, ShieldCheck, Bell } from 'lucide-react';

const CARDS = [
  {
    id: 'efectivo',
    icon: Banknote,
    title: 'Pago en efectivo',
    desc: 'Paga al recibir tu pedido, sin necesidad de tarjeta ni cuenta bancaria. Simple y directo.',
    gradient: 'from-orange-500/8 to-transparent',
    glow: 'group-hover:shadow-orange-500/10',
    accent: 'border-orange-500/20 bg-orange-500/10 text-orange-400 group-hover:bg-orange-500/15 group-hover:border-orange-500/35',
    badge: null,
  },
  {
    id: 'mercadopago',
    icon: CreditCard,
    title: 'Mercado Pago',
    desc: 'Paga con tarjeta de crédito o débito, OXXO, transferencia SPEI o en meses sin intereses — todo desde la plataforma más usada en México.',
    gradient: 'from-[#009EE3]/6 to-transparent',
    glow: 'group-hover:shadow-[#009EE3]/10',
    accent: 'border-[#009EE3]/20 bg-[#009EE3]/10 text-[#009EE3] group-hover:bg-[#009EE3]/20 group-hover:border-[#009EE3]/35',
    badge: ['Visa', 'Mastercard', 'OXXO', 'SPEI', 'Amex'],
  },
  {
    id: 'seguro',
    icon: ShieldCheck,
    title: 'Pago 100% seguro',
    desc: 'Todos los pagos con tarjeta se procesan a través de Mercado Pago. Nunca almacenamos tu información bancaria.',
    gradient: 'from-emerald-500/6 to-transparent',
    glow: 'group-hover:shadow-emerald-500/10',
    accent: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-400 group-hover:bg-emerald-500/15 group-hover:border-emerald-500/35',
    badge: null,
  },
  {
    id: 'notificacion',
    icon: Bell,
    title: 'Te avisamos en cada paso',
    desc: 'Recibes notificaciones cuando tu pedido es aceptado, cuando va en camino y cuando es entregado. Siempre informado.',
    gradient: 'from-orange-600/8 to-transparent',
    glow: 'group-hover:shadow-orange-600/10',
    accent: 'border-orange-500/20 bg-orange-500/10 text-orange-400 group-hover:bg-orange-500/15 group-hover:border-orange-500/35',
    badge: null,
  },
];

export default function PagosPage() {
  return (
    <div className="min-h-[calc(100vh-160px)] bg-[#0a0a0a] pb-28">

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-orange-500/10 blur-[120px]" />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-orange-600/5 blur-[80px]" />
        </div>
        <div
          className="absolute inset-0 opacity-[0.015] pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)', backgroundSize: '48px 48px' }}
        />
        <div className="relative max-w-4xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-14 md:pb-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-4 py-1.5 mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
            <span className="text-[10px] font-black tracking-[0.25em] uppercase text-orange-300">
              Pagos seguros y transparentes
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05]">
            Tu pago,{' '}
            <span className="relative inline-block">
              <span className="text-orange-400">a tu manera.</span>
              <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-orange-500/0 via-orange-500/60 to-orange-500/0" />
            </span>
          </h1>
          <p className="mt-5 text-white/50 text-base md:text-lg max-w-lg mx-auto leading-relaxed">
            En NubesMty operamos con total transparencia.{' '}
            <span className="text-white/70">Sin sorpresas, sin cobros ocultos.</span>
          </p>
        </div>
      </section>

      {/* ── Cards ── */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-14 md:pt-18">
        <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
          {CARDS.map((c, i) => {
            const Ic = c.icon;
            return (
              <div
                key={c.id}
                data-testid={`payment-card-${c.id}`}
                style={{ animationDelay: `${i * 60}ms` }}
                className={`group relative rounded-2xl border border-white/[0.07] bg-gradient-to-br ${c.gradient} bg-[#111111] overflow-hidden
                  transition-all duration-300 hover:border-orange-500/25 hover:shadow-2xl ${c.glow} p-7 md:p-8`}
              >
                <div className="absolute -top-12 -right-12 w-36 h-36 rounded-full bg-orange-500/5 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-colors duration-300 ${c.accent}`}>
                    <Ic className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-black tracking-[0.2em] uppercase text-white/15 group-hover:text-orange-400/50 transition-colors duration-300 mt-1">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-black text-white mb-2.5 leading-tight">{c.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed group-hover:text-white/60 transition-colors duration-300">{c.desc}</p>
                {c.badge && (
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {c.badge.map((m) => (
                      <span key={m} className="px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/10 text-[10px] font-bold text-white/50">{m}</span>
                    ))}
                  </div>
                )}
                <div className="mt-6 h-px bg-gradient-to-r from-orange-500/0 via-orange-500/0 to-orange-500/0 group-hover:from-orange-500/0 group-hover:via-orange-500/30 group-hover:to-orange-500/0 transition-all duration-500" />
              </div>
            );
          })}
        </div>

        {/* ── Trust line ── */}
        <div className="mt-12 flex items-center gap-5">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <p className="text-white/30 text-xs font-medium tracking-wide text-center whitespace-nowrap px-2">
            Más de 500 clientes satisfechos en Monterrey confían en NubesMty.
          </p>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>
      </section>
    </div>
  );
}
